import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ToastsService } from '@tgx/shared/toasts';
import {
  AdminService,
  AuthService,
  CacheService,
  PlatformInfoService,
  UserManagementService,
  UserProfileService,
} from '@tgx/shared/services';
import {
  ContactInfo as OrgContactInfo,
  AdminMemberProfile,
  ContactTypeData,
  MemberInfo,
  SelectorOrganization,
} from '@tgx/shared/interfaces';
import { FormValidator } from '@tgx/shared/utils';
import {
  ContactTypeString,
  PermissionsService,
  ContactInfo,
  MembersRole,
  PartnerType,
  ContactType,
} from '@tgx/shared/enums';
import { Subject, takeUntil } from 'rxjs';
import { MetaHelpSliderItem, XenAlertType } from '@tgx/shared/ui/storybook';
import { DOCUMENTATION_LINKS } from '@tgx/shared/constants';
import { ContactTypeForm, MappedMemberRole } from '../../constants/users-permissions.constants';
import { PermissionsUpdate } from '../../interfaces/permissions-configuration.interface';
import { CompanyContactsService } from '../../services/company-contacts.service';
import { UserPermissionsService } from '../../services/user-permissions.service';

@Component({
  selector: 'tgx-user-invite-modal',
  templateUrl: './user-invite-modal.component.html',
  styleUrls: ['./user-invite-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserInviteModalComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private activeModal = inject(NgbActiveModal);
  private toast = inject(ToastsService);
  private platformInfoService = inject(PlatformInfoService);
  private cacheService = inject(CacheService);
  private adminService = inject(AdminService);
  private userProfileService = inject(UserProfileService);
  private userManagementService = inject(UserManagementService);
  private settingsCompanyContactsService = inject(CompanyContactsService);
  private settingsPermissionsService = inject(UserPermissionsService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);

  @Input() orgUsersList: string[];
  @Input() editUser = false;
  @Input() canEditRoles: boolean;
  @Input() editRolesTooltipText: string;
  @Input() canEditContacts: boolean;
  @Input() editContactsTooltipText: string;
  private orgKeyContacts: OrgContactInfo = { buyerKeyContacts: [], sellerKeyContacts: [] };
  protected showBuyerBillingInput = false;
  protected showSellerBillingInput = false;

  private _user: MemberInfo;
  @Input() set user(value: MemberInfo) {
    this._user = value;
    // Only execute if we're already in OnInit (forms created)
    if (this.editUser && value && this.userInfoForm) {
      this.populateForms();
    }
  }
  get user(): MemberInfo {
    return this._user;
  }

  protected currentStep = 1;
  protected isUserExisting = false;
  protected showUserInputs = false;

  protected emailForm: FormGroup;
  protected userInfoForm: FormGroup;

  protected buyerContactsForm: FormGroup;
  protected sellerContactsForm: FormGroup;

  protected profileFormBackup = {} as MemberInfo;
  protected userToCreate = {} as MemberInfo;

  private buyerContactsBackup: ContactTypeData[] = [];
  private sellerContactsBackup: ContactTypeData[] = [];
  private contactsToAdd: ContactTypeData[] = [];
  private contactsToDelete: ContactTypeData[] = [];
  protected contactsLoaded = false;

  protected isLoading: boolean;
  protected isSendLoading = false;

  protected orgCode: string;
  protected orgCrmId: string;
  protected isBuyer = false;
  protected isSeller = false;
  protected hasSameBillingAccount = false;

  protected titleModals = [
    {
      id: 1,
      title: 'Enter a valid email to send the invitation',
      subtitle: 'At this address the user will receive the link to complete the registration',
      active: true,
    },
    {
      id: 2,
      title: 'Tell us more about this user',
      subtitle: 'It is important to know about our users to offer a better service',
      active: false,
    },
    {
      id: 3,
      title: 'Configure permissions for this user',
      subtitle: "The user's role will determine what and how Travelgate will be used.",
      active: false,
    },
  ];

  protected mainContacts = [
    {
      label: ContactInfo.API_IMPLEMENTATION,
      controlName: ContactType.API_IMPLEMENTATION,
      tooltip: 'Owns commercial terms & relationship',
    },
    {
      label: ContactInfo.TECH_SUPPORT,
      controlName: ContactType.TECH_SUPPORT,
      tooltip: 'Handles day-to-day technical operations & gets notified of any new connection activation requests',
    },
    {
      label: ContactInfo.TECH_DECISION,
      controlName: ContactType.ACTIVATION,
      tooltip: 'Stay informed about API updates and technical changes that impact your booking flow',
    },
    {
      label: ContactInfo.BILLING,
      controlName: ContactType.BILLING,
      tooltip: 'Contact for invoicing and billing matters',
    },
  ];

  protected steps = [
    { id: 1, label: 'User Email', current: true, status: 'default', edit: false, child: false, level: 'parent' },
    { id: 2, label: 'User Data', current: false, status: 'default', edit: false, child: false, level: 'parent' },
    { id: 3, label: 'User Permissions', current: false, status: 'default', edit: false, child: false, level: 'parent' },
  ];

  protected metaHelp: MetaHelpSliderItem[] = [
    {
      title: 'Quick guide to user roles',
      text: 'Explore the different roles, meanings, and find quick solutions.',
      link: DOCUMENTATION_LINKS.settings.users,
    },
  ];

  protected alertTypeInfo = XenAlertType.Info;
  protected alertTypeWarning = XenAlertType.Warning;
  protected isPermissionsFormInvalid = true;
  private isBillingSelectionInvalid = false;
  protected readonly ContactType = ContactType;
  private destroy$ = new Subject<void>();

  protected get modalTitle(): string {
    if (!this.editUser) {
      return 'Invite a user';
    }

    const firstName = this.user?.firstName?.trim() ?? '';
    const lastName = this.user?.lastName?.trim() ?? '';
    const fullName = this.user?.fullName?.trim() ?? '';
    const displayName = `${firstName} ${lastName}`.trim() || fullName || this.user?.email || 'user';

    return `Edit ${displayName}`;
  }

  protected get finishDisabled(): boolean {
    if (this.currentStep === 3 && !this.editUser) return this.isPermissionsFormInvalid;
    if (this.currentStep === 3 && this.editUser) return this.isBillingSelectionInvalid || !this.hasChanges;
    return false;
  }

  private get hasChanges(): boolean {
    if (!this.editUser) return true;
    const jobTitleChanged = this.userInfoForm.get('jobTitle')?.value !== this.profileFormBackup.jobTitle;
    const permissionsChanged = Object.entries(this.userToCreate).some(
      ([prop, value]) => value !== this.profileFormBackup[prop],
    );
    const buyerChanged = this.checkFormChanged(this.buyerContactsForm, this.buyerContactsBackup);
    const sellerChanged = this.checkFormChanged(this.sellerContactsForm, this.sellerContactsBackup);
    return jobTitleChanged || permissionsChanged || buyerChanged || sellerChanged;
  }

  private checkFormChanged(form: FormGroup, backup: ContactTypeData[]): boolean {
    if (!form) return false;
    const currentValues = form.getRawValue();
    return Object.entries(currentValues).some(([key, isChecked]) => {
      const contactType = this.mapFormKeyToContactTypeString(key as ContactType);
      const wasInBackup = backup.some((c) => c.contactType === contactType);
      return isChecked !== wasInBackup;
    });
  }

  ngOnInit() {
    this.buildEmailForm();
    this.buildUserInfoForm();
    this.buildContactsForms();

    this.settingsCompanyContactsService.hasSameBillingAccount
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasSameBillingAccount) => {
        this.hasSameBillingAccount = !!hasSameBillingAccount;
      });

    this.settingsCompanyContactsService.showBuyerBillingInput
      .pipe(takeUntil(this.destroy$))
      .subscribe((showBuyerBillingInput) => {
        this.showBuyerBillingInput = !!showBuyerBillingInput;
      });

    this.settingsCompanyContactsService.showSellerBillingInput
      .pipe(takeUntil(this.destroy$))
      .subscribe((showSellerBillingInput) => {
        this.showSellerBillingInput = !!showSellerBillingInput;
      });

    // Edit mode: populate forms with user data
    if (this.editUser && this.user) {
      this.isPermissionsFormInvalid = false;
      this.populateForms();
    }

    this.platformInfoService.organizationSelected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (org: SelectorOrganization) => {
        if (!org) return;
        this.isBuyer = org.isBuyer;
        this.isSeller = org.isSeller;
        this.orgCode = org.code;
        const profiles = await this.cacheService.retrieveProfilesByCodes([org.code]);
        this.orgCrmId = profiles[0]?.crmId;
        // Only initialize contacts if we're in edit mode or if the email is already valid in create mode
        if (this.editUser && this.user?.email) {
          this.initializeCompanyContacts(this.orgCode, this.isBuyer, this.isSeller);
        }
        this.cdr.detectChanges();
      });

    this.emailForm
      .get('email')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value && this.emailForm.get('email').valid && !this.editUser) {
          this.initializeCompanyContacts(this.orgCode, this.isBuyer, this.isSeller);
        }
      });
  }

  private buildEmailForm() {
    this.emailForm = this.fb.group({
      email: ['', [FormValidator.emailRegex, Validators.required, this.customEmailValidator(this.orgUsersList)]],
    });
  }

  private buildUserInfoForm() {
    this.userInfoForm = this.fb.group({
      firstName: [
        '',
        [
          FormValidator.required('name'),
          FormValidator.textNumbers('Name'),
          FormValidator.maxLength('Name', 20),
          FormValidator.textLength('Name', 3),
        ],
      ],
      lastName: [
        '',
        [
          FormValidator.required('last name'),
          FormValidator.textNumbers('Last name'),
          FormValidator.maxLength('Last name', 20),
          FormValidator.textLength('Last name', 3),
        ],
      ],
      jobTitle: [
        '',
        [
          FormValidator.textNumbers('Job Title'),
          FormValidator.textLength('Job Title', 3),
          FormValidator.maxLength('Job Title', 50),
        ],
      ],
    });
  }

  private buildContactsForms() {
    const controls = {
      [ContactType.API_IMPLEMENTATION]: [false],
      [ContactType.TECH_SUPPORT]: [false],
      [ContactType.ACTIVATION]: [false],
      [ContactType.BILLING]: [false],
    };
    this.buyerContactsForm = this.fb.group({ ...controls });
    this.sellerContactsForm = this.fb.group({ ...controls });
    this.contactsLoaded = false;
  }

  private populateForms() {
    if (!this.user || !this.userInfoForm) return;

    // Force step 2 for Edit Mode
    this.currentStep = 2;
    this.updateStepsNavigation(2);

    const firstName = this.user.firstName ?? this.user.fullName?.split(' ')[0] ?? '';
    const lastName = this.user.lastName ?? this.user.fullName?.split(' ')[1] ?? '';
    const jobTitle = this.user.jobTitle ?? '';

    this.userInfoForm.patchValue({ firstName, lastName, jobTitle });

    this.profileFormBackup = {
      ...this.profileFormBackup,
      firstName,
      lastName,
      jobTitle,
      email: this.user.email,
      role: this.user.role,
    };
    this.userToCreate = { email: this.user.email };
  }

  protected initialUserPermissions(allMemberPermissions: PermissionsUpdate) {
    this.applyPermissionsToTarget(this.profileFormBackup, allMemberPermissions);
  }

  protected updatePermissions(memberPermissions: PermissionsUpdate) {
    this.applyPermissionsToTarget(this.userToCreate, memberPermissions);
  }

  private applyPermissionsToTarget(target: MemberInfo, permissions: PermissionsUpdate) {
    if (!permissions) return;

    for (const [service, role] of Object.entries(permissions)) {
      const prop = MappedMemberRole[service];
      if (prop) {
        if (service === PermissionsService.BILLING && !role) {
          const initialBillingRole = this.profileFormBackup[prop];
          if (initialBillingRole === undefined || initialBillingRole === null) {
            // Billing started disabled: removing the key avoids a false "changed" state.
            delete target[prop];
          } else {
            // Billing started enabled: keep null so save flow triggers removeMemberServiceRole.
            target[prop] = null;
          }
          continue;
        }

        target[prop] = role;
      }
    }
  }

  protected async onSubmit(editMember: boolean) {
    if (this.finishDisabled || this.isSendLoading) return;
    this.isLoading = true;
    this.isSendLoading = true;
    if (editMember) {
      await this.updateMember();
    } else {
      await this.addMember();
    }
  }

  private async updateMember() {
    const email = this.user.email;
    const currentValues: MemberInfo = {
      ...this.profileFormBackup,
      ...this.userInfoForm.getRawValue(),
      ...this.userToCreate,
    };

    let rolesChanged: boolean;
    try {
      if (currentValues.jobTitle !== this.profileFormBackup.jobTitle) {
        await this.adminService.updateMemberDataInOrganization(email, this.orgCode, {
          jobTitle: currentValues.jobTitle ?? '',
        });
      }
      rolesChanged = await this.processServiceRoles(email, currentValues, true);
      await this.updateContactsIfChanged();

      this.toast.addToast('Success', 'bg-success', 'User updated successfully.');
      const result = {
        email,
        ...currentValues,
        role: currentValues.role || this.user.role,
        keyContactChanges: this.buildKeyContactChangesPayload(),
      };
      this.activeModal.close(result);
    } catch {
      this.toast.addToast('Error', 'bg-danger', 'Failed to update user.');
      return;
    } finally {
      this.isSendLoading = false;
      this.isLoading = false;
    }

    // Only permission changes require repainting; name/jobTitle/contacts do not.
    if (rolesChanged) await this.syncAffectedUserSession(email);
  }

  // Propagate permission changes to the affected user's live session(s).
  private async syncAffectedUserSession(affectedEmail: string): Promise<void> {
    if (!affectedEmail) return;
    // Flag every live session of the affected user (web, admin, other tabs) so each refreshes on next validate.
    await this.authService.flagUserForRefresh(affectedEmail);
    // If the change targets the current session's user, refresh now for immediate feedback.
    const currentIdentity = this.platformInfoService.getImpersonatedEmail() || this.platformInfoService.getEmail();
    if (currentIdentity && affectedEmail.toLowerCase() === currentIdentity.toLowerCase()) {
      await this.authService.refreshToken();
    }
  }

  private async addMember() {
    const user: MemberInfo = {
      email: this.emailForm.getRawValue().email,
      ...this.userInfoForm.getRawValue(),
      ...this.userToCreate,
      role: MembersRole.VIEWER,
    };

    try {
      const res = await this.userManagementService.addMember(
        this.orgCode,
        this.orgCrmId,
        user,
        this.buildUserCreationInput(user.email),
        this.buildUserUpdateInput(),
      );
      if (res && res[0]) {
        await this.processServiceRoles(res[0], user, false);
        await this.updateContactsIfChanged();
        this.toast.addToast('Success', 'bg-success', `User created successfully.`);
        const result = {
          ...user,
          keyContactChanges: this.buildKeyContactChangesPayload(),
        };
        this.activeModal.close(result);
      }
    } catch {
      this.toast.addToast('Error', 'bg-danger', 'Error creating user.');
    } finally {
      this.isSendLoading = false;
      this.isLoading = false;
    }
  }

  private async processServiceRoles(email: string, currentData: MemberInfo, isUpdate: boolean): Promise<boolean> {
    const { failedServices, rolesChanged } = await this.settingsPermissionsService.applyServiceRoles(
      email,
      this.orgCode,
      currentData,
      this.profileFormBackup,
      isUpdate,
    );

    if (failedServices.length > 0) {
      this.toast.addToast(
        'Warning',
        'bg-warning',
        `Some service roles could not be updated: ${failedServices.join(', ')}`,
      );
    }

    return rolesChanged;
  }

  protected goToPreviousStep(goStep: number) {
    this.currentStep = goStep;
    this.updateStepsNavigation(goStep);
  }

  protected editStep(stepEdit: { id: number; edit: boolean }) {
    if (stepEdit?.edit) {
      this.currentStep = stepEdit.id;
      this.updateStepsNavigation(this.currentStep);
    }
  }

  private updateStepsNavigation(targetStep: number) {
    this.steps.forEach((step) => {
      step.current = step.id === targetStep;
      if (step.id < targetStep) {
        step.status = 'success';
        // This line prevents clicking back to Step 1 if editing
        step.edit = !(this.editUser && step.id === 1);
      } else {
        step.status = 'default';
        step.edit = false;
      }
    });
    this.updateTitleModals();
  }

  private updateTitleModals() {
    this.titleModals.forEach((title, index) => (title.active = index + 1 === this.currentStep));
  }

  protected async onSubmitEmail(): Promise<void> {
    if (this.emailForm.invalid) return;
    await this.updateUserExistenceStatus(this.emailForm.value.email);
    this.currentStep = 2;
    this.updateStepsNavigation(2);
  }

  protected onSubmitUserInfo() {
    if (this.userInfoForm.valid) {
      this.currentStep = 3;
      this.updateStepsNavigation(3);
    }
  }

  private async updateUserExistenceStatus(email: string): Promise<void> {
    this.isLoading = true;
    try {
      const memberProfile: AdminMemberProfile | null =
        await this.userProfileService.getAdminMemberProfileByEmail(email);
      if (memberProfile?.code) {
        this.isUserExisting = true;
        this.userInfoForm.patchValue({
          firstName: memberProfile.firstName ?? '',
          lastName: memberProfile.lastName ?? '',
        });
        this.userInfoForm.get('firstName').disable();
        this.userInfoForm.get('lastName').disable();
      } else {
        this.isUserExisting = false;
        this.userInfoForm.get('firstName').enable();
        this.userInfoForm.get('lastName').enable();
      }
    } catch {
      this.isUserExisting = false;
    } finally {
      this.isLoading = false;
    }
  }

  private async initializeCompanyContacts(orgCode: string, isBuyer: boolean, isSeller: boolean) {
    if (!orgCode) return;
    try {
      const targetEmail = this.editUser ? this.user?.email : this.emailForm.get('email').value;
      if (!targetEmail) return;

      this.orgKeyContacts = await this.settingsCompanyContactsService.getKeyContacts(orgCode, isBuyer, isSeller);

      this.buyerContactsBackup = [];
      this.sellerContactsBackup = [];

      this.buildMainContacts(this.orgKeyContacts?.buyerKeyContacts || [], 'BUYER', targetEmail);
      this.buildMainContacts(this.orgKeyContacts?.sellerKeyContacts || [], 'SELLER', targetEmail);
      this.contactsLoaded = true;
    } catch {
      this.toast.addToast('Error', 'bg-danger', 'Company contact information could not be loaded.');
      this.contactsLoaded = true;
    }
  }

  private buildMainContacts(contacts: ContactTypeData[], sourcePartnerType: 'BUYER' | 'SELLER', targetEmail: string) {
    contacts.forEach((contact) => {
      if (contact.email?.toLowerCase() === targetEmail?.toLowerCase()) {
        const partnerType = contact.partnerType || sourcePartnerType;
        const form = partnerType === 'SELLER' ? this.sellerContactsForm : this.buyerContactsForm;
        const controlName = ContactTypeForm[contact.contactType] || contact.contactType;
        if (form && form.get(controlName)) {
          form.get(controlName).setValue(true);
        }
        if (partnerType === 'SELLER') this.sellerContactsBackup.push(contact);
        else this.buyerContactsBackup.push(contact);
      }
    });
  }

  private customEmailValidator(orgUsersList: string[]): ValidatorFn {
    return (control: AbstractControl) =>
      orgUsersList?.includes(control.value)
        ? { userAlreadyExists: true, errorMsg: 'This email already exists in your organization.' }
        : null;
  }

  private buildUserCreationInput(email: string) {
    const { firstName, lastName, jobTitle } = this.userInfoForm.getRawValue();
    return { email, firstName, lastName, partnerIncludedInInput: { orgCode: this.orgCode, jobTitle } };
  }

  private buildUserUpdateInput() {
    return { add: [{ jobTitle: this.userInfoForm.getRawValue().jobTitle, orgCode: this.orgCode }] };
  }

  protected getValidationStatus(controlName: string): string {
    const control = this.userInfoForm?.get(controlName);
    return !control || !control.dirty ? '' : control.invalid ? 'is-invalid' : 'is-valid';
  }

  protected changeShowUserInputs(event: boolean) {
    this.showUserInputs = event;
  }

  protected handleInvalidForm(isInvalid: boolean): void {
    this.isPermissionsFormInvalid = isInvalid;
  }

  protected handleBillingSelectionInvalid(isInvalid: boolean): void {
    this.isBillingSelectionInvalid = isInvalid;
  }

  protected closeModal() {
    this.activeModal.dismiss();
  }

  private async updateContactsIfChanged(): Promise<void> {
    this.contactsToAdd = [];
    this.contactsToDelete = [];
    if (this.isBuyer)
      this.identifyBuyerSellerContactChanges(
        this.buyerContactsForm.getRawValue(),
        this.buyerContactsBackup,
        PartnerType.BUYER,
      );
    if (this.isSeller)
      this.identifyBuyerSellerContactChanges(
        this.sellerContactsForm.getRawValue(),
        this.sellerContactsBackup,
        PartnerType.SELLER,
      );

    if (this.contactsToAdd.length > 0 || this.contactsToDelete.length > 0) {
      try {
        const filterByRole = (contacts: ContactTypeData[], role: PartnerType) =>
          contacts.filter((c) => c.role === role).map(({ role, ...rest }) => rest);
        await this.settingsCompanyContactsService.updateKeyContacts(
          this.orgCode,
          filterByRole(this.contactsToAdd, PartnerType.BUYER),
          filterByRole(this.contactsToDelete, PartnerType.BUYER),
          filterByRole(this.contactsToAdd, PartnerType.SELLER),
          filterByRole(this.contactsToDelete, PartnerType.SELLER),
        );
      } catch (err) {
        console.error('Error updating contacts:', err);
        this.toast.addToast('Warning', 'bg-warning', "We couldn't update the user's company contacts.");
      }
    }
  }

  private identifyBuyerSellerContactChanges(
    currentContactsForm: Record<ContactType, boolean>,
    backupContacts: ContactTypeData[],
    partnerType: PartnerType,
  ): void {
    const email = this.editUser ? this.user.email : this.emailForm.get('email').value;
    for (const [formKey, isChecked] of Object.entries(currentContactsForm)) {
      const contactType = this.mapFormKeyToContactTypeString(formKey as ContactType);
      if (!contactType) continue;
      const existingContact = backupContacts.find((c) => c.contactType === contactType);
      if (isChecked && !existingContact) this.contactsToAdd.push({ contactType, email, role: partnerType });
      if (!isChecked && existingContact) this.contactsToDelete.push({ contactType, email, role: partnerType });
    }
  }

  private mapFormKeyToContactTypeString(formKey: ContactType): ContactTypeString | null {
    const mapping = {
      [ContactType.API_IMPLEMENTATION]: ContactTypeString.API_IMPLEMENTATION,
      [ContactType.TECH_SUPPORT]: ContactTypeString.TECH_SUPPORT,
      [ContactType.BILLING]: ContactTypeString.BILLING,
      [ContactType.ACTIVATION]: ContactTypeString.ACTIVATION,
    };
    return mapping[formKey] || null;
  }

  private buildKeyContactChangesPayload(): { add: ContactTypeData[]; remove: ContactTypeData[] } {
    return {
      add: this.formatContacts(this.contactsToAdd),
      remove: this.formatContacts(this.contactsToDelete),
    };
  }

  private formatContacts(contacts: ContactTypeData[]) {
    return contacts.map((contact) => ({
      email: contact.email,
      contactType: contact.contactType,
      partnerType: contact.role,
    }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
